import { useState } from 'react';
import { Title, useNotify } from 'react-admin';
import { useQuery, useMutation } from '@apollo/client';
import { useParams, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import Skeleton from '@mui/material/Skeleton';
import Alert from '@mui/material/Alert';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import Divider from '@mui/material/Divider';
import LinearProgress from '@mui/material/LinearProgress';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CancelIcon from '@mui/icons-material/Cancel';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';

import {
  ADMIN_GET_MERCHANT_CATALOG_PLAN,
  ADMIN_FORCE_DOWNGRADE_CATALOG_PLAN,
  ADMIN_CANCEL_CATALOG_PLAN,
} from './merchant-catalog-plan.queries';
import {
  MerchantCatalogPlan,
  PLAN_LABELS,
  PLAN_COLORS,
  STATUS_LABELS,
  STATUS_COLORS,
  formatDate,
  formatDateTime,
  trialDaysRemaining,
} from './merchant-catalog-plan.types';

// ─── Info Row ─────────────────────────────────────────────────────────────────

function InfoRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.75, borderBottom: '1px solid #f0f0f0' }}>
      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 160 }}>{label}</Typography>
      <Typography variant="body2" fontWeight={500} textAlign="right">{value ?? '—'}</Typography>
    </Box>
  );
}

// ─── Force Downgrade Dialog ───────────────────────────────────────────────────

interface ActionDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => void;
  loading: boolean;
  title: string;
  description: string;
  confirmLabel: string;
  confirmColor?: 'error' | 'warning' | 'primary';
}

function ActionDialog({ open, onClose, onConfirm, loading, title, description, confirmLabel, confirmColor = 'error' }: ActionDialogProps) {
  const [reason, setReason] = useState('');

  const handleConfirm = () => {
    onConfirm(reason);
    setReason('');
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{description}</Typography>
        <TextField
          label="Raison (optionnelle)"
          fullWidth
          multiline
          rows={2}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          size="small"
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Annuler</Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color={confirmColor}
          disabled={loading}
        >
          {loading ? 'En cours…' : confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Main Detail ──────────────────────────────────────────────────────────────

export function MerchantCatalogPlanDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const notify = useNotify();

  const [downgradeOpen, setDowngradeOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);

  const { data, loading, error, refetch } = useQuery<{
    adminMerchantCatalogPlan: MerchantCatalogPlan;
  }>(ADMIN_GET_MERCHANT_CATALOG_PLAN, {
    variables: { id },
    skip: !id,
  });

  const [forceDowngrade, { loading: downgrading }] = useMutation(
    ADMIN_FORCE_DOWNGRADE_CATALOG_PLAN,
    {
      onCompleted: () => {
        notify('Plan rétrogradé vers FREE', { type: 'success' });
        setDowngradeOpen(false);
        refetch();
      },
      onError: (err) => notify(`Erreur: ${err.message}`, { type: 'error' }),
    },
  );

  const [cancelPlan, { loading: cancelling }] = useMutation(ADMIN_CANCEL_CATALOG_PLAN, {
    onCompleted: () => {
      notify('Plan annulé', { type: 'success' });
      setCancelOpen(false);
      refetch();
    },
    onError: (err) => notify(`Erreur: ${err.message}`, { type: 'error' }),
  });

  const plan = data?.adminMerchantCatalogPlan;
  const merchant = plan?.digitalCard;
  const user = merchant?.user;
  const referralCode = plan?.referralCode;
  const daysLeft = trialDaysRemaining(plan?.trialEndsAt);

  if (loading && !plan) {
    return (
      <Box sx={{ p: 3 }}>
        <Skeleton variant="rectangular" height={60} sx={{ mb: 2 }} />
        <Skeleton variant="rectangular" height={200} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error.message}</Alert>
      </Box>
    );
  }

  if (!plan) return null;

  return (
    <Box sx={{ p: 3 }}>
      <Title title={`Catalogue — ${merchant?.displayName ?? plan.digitalCardId}`} />

      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/merchant-catalog-plans')}
          variant="text"
          sx={{ mr: 1 }}
        >
          Retour
        </Button>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" fontWeight={700}>
            {merchant?.displayName ?? '—'}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
            <Chip
              label={PLAN_LABELS[plan.planCode] ?? plan.planCode}
              color={PLAN_COLORS[plan.planCode] ?? 'default'}
              size="small"
              variant="outlined"
            />
            <Chip
              label={STATUS_LABELS[plan.status] ?? plan.status}
              color={STATUS_COLORS[plan.status] ?? 'default'}
              size="small"
            />
            {plan.isTrialActive && (
              <Chip
                label={`Essai — ${daysLeft}j restants`}
                color="warning"
                size="small"
                variant="outlined"
              />
            )}
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {plan.isTrialActive && (
            <Button
              variant="outlined"
              color="warning"
              size="small"
              startIcon={<TrendingDownIcon />}
              onClick={() => setDowngradeOpen(true)}
            >
              Forcer downgrade
            </Button>
          )}
          {plan.status !== 'CANCELLED' && plan.status !== 'EXPIRED' && (
            <Button
              variant="outlined"
              color="error"
              size="small"
              startIcon={<CancelIcon />}
              onClick={() => setCancelOpen(true)}
            >
              Annuler
            </Button>
          )}
        </Box>
      </Box>

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      <Grid container spacing={3}>
        {/* Col gauche */}
        <Grid item xs={12} md={6}>
          {/* Plan Info */}
          <Card sx={{ mb: 2 }}>
            <CardHeader title="Abonnement Catalogue" titleTypographyProps={{ variant: 'subtitle1', fontWeight: 700 }} />
            <CardContent>
              <InfoRow label="Plan" value={PLAN_LABELS[plan.planCode] ?? plan.planCode} />
              <InfoRow label="Statut" value={
                <Chip label={STATUS_LABELS[plan.status]} color={STATUS_COLORS[plan.status] ?? 'default'} size="small" />
              } />
              <InfoRow label="Devise facturation" value={`${plan.billingCurrency} (${plan.billingCountry})`} />
              <InfoRow label="Période courante début" value={formatDate(plan.currentPeriodStart)} />
              <InfoRow label="Période courante fin" value={formatDate(plan.currentPeriodEnd)} />
              <InfoRow label="Annulation en fin de période" value={plan.cancelAtPeriodEnd ? 'Oui' : 'Non'} />
              {plan.canceledAt && <InfoRow label="Annulé le" value={formatDateTime(plan.canceledAt)} />}
              <InfoRow label="Créé le" value={formatDateTime(plan.createdAt)} />
              <InfoRow label="Modifié le" value={formatDateTime(plan.updatedAt)} />
            </CardContent>
          </Card>

          {/* Trial Info */}
          {(plan.isTrialActive || plan.trialStartedAt) && (
            <Card sx={{ mb: 2 }}>
              <CardHeader title="Essai PRO" titleTypographyProps={{ variant: 'subtitle1', fontWeight: 700 }} />
              <CardContent>
                <InfoRow label="Actif" value={plan.isTrialActive ? 'Oui' : 'Non'} />
                <InfoRow label="Démarré le" value={formatDateTime(plan.trialStartedAt)} />
                <InfoRow label="Se termine le" value={formatDate(plan.trialEndsAt)} />
                {plan.isTrialActive && (
                  <InfoRow label="Jours restants" value={
                    <Typography variant="body2" fontWeight={700} color={daysLeft <= 3 ? 'error.main' : 'warning.main'}>
                      {daysLeft} jours
                    </Typography>
                  } />
                )}
              </CardContent>
            </Card>
          )}
        </Grid>

        {/* Col droite */}
        <Grid item xs={12} md={6}>
          {/* Merchant Info */}
          <Card sx={{ mb: 2 }}>
            <CardHeader title="Boutique Commerce" titleTypographyProps={{ variant: 'subtitle1', fontWeight: 700 }} />
            <CardContent>
              <InfoRow label="Nom de la boutique" value={merchant?.displayName} />
              <InfoRow label="Slug" value={merchant?.slug} />
              <InfoRow label="Type de carte" value={merchant?.cardType} />
              <InfoRow label="ID Carte" value={
                <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                  {plan.digitalCardId}
                </Typography>
              } />
              <Divider sx={{ my: 1 }} />
              {user && (
                <>
                  <InfoRow label="Marchand" value={`${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() || '—'} />
                  <InfoRow label="Email" value={user.email} />
                  <InfoRow label="Téléphone" value={user.phoneNumber} />
                  <InfoRow label="ID User" value={
                    <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{user.id}</Typography>
                  } />
                </>
              )}
            </CardContent>
          </Card>

          {/* Referral Info */}
          <Card sx={{ mb: 2 }}>
            <CardHeader title="Parrainage" titleTypographyProps={{ variant: 'subtitle1', fontWeight: 700 }} />
            <CardContent>
              <InfoRow label="Parrainages confirmés" value={`${plan.referralsEarned}/5`} />
              <InfoRow label="Bonus articles" value={plan.referralBonusProducts > 0 ? `+${plan.referralBonusProducts}` : '0'} />
              <InfoRow label="Bonus photo parrainé" value={plan.hasReferralPhotoBonus ? 'Oui (+1 photo/produit)' : 'Non'} />
              {referralCode && (
                <>
                  <Divider sx={{ my: 1 }} />
                  <InfoRow label="Code de parrainage" value={
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                      {referralCode.code}
                    </Typography>
                  } />
                  <InfoRow label="Clics" value={referralCode.totalClicks} />
                  <InfoRow label="Inscriptions" value={referralCode.totalSignups} />
                  <InfoRow label="Total bonus distribués" value={referralCode.totalEarned} />
                </>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Referrals table */}
        {referralCode?.referrals && referralCode.referrals.length > 0 && (
          <Grid item xs={12}>
            <Card>
              <CardHeader
                title="Filleuls"
                titleTypographyProps={{ variant: 'subtitle1', fontWeight: 700 }}
                subheader={`${referralCode.referrals.length} filleul(s)`}
              />
              <CardContent sx={{ p: 0 }}>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ background: '#f5f5f5' }}>
                        <TableCell sx={{ fontWeight: 700 }}>Filleul</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Email</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Statut</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Date inscription</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Récompensé le</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(referralCode.referrals as any[]).map((r: any) => (
                        <TableRow key={r.id} hover>
                          <TableCell>
                            {r.referred
                              ? `${r.referred.firstName ?? ''} ${r.referred.lastName ?? ''}`.trim() || '—'
                              : '—'}
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption">{r.referred?.email ?? '—'}</Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={r.status}
                              size="small"
                              color={r.status === 'ACTIVE' ? 'success' : r.status === 'EXPIRED' ? 'error' : 'default'}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption">{formatDate(r.createdAt)}</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption">{formatDate(r.rewardedAt)}</Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      {/* Dialogs */}
      <ActionDialog
        open={downgradeOpen}
        onClose={() => setDowngradeOpen(false)}
        onConfirm={(reason) => forceDowngrade({ variables: { planId: plan.id, reason: reason || 'Admin action' } })}
        loading={downgrading}
        title="Forcer le downgrade vers FREE"
        description="Cette action met fin au trial en cours et rétrograde le plan vers CATALOGUE_FREE. Les produits au-delà du quota seront masqués."
        confirmLabel="Forcer downgrade"
        confirmColor="warning"
      />

      <ActionDialog
        open={cancelOpen}
        onClose={() => setCancelOpen(false)}
        onConfirm={(reason) => cancelPlan({ variables: { planId: plan.id, reason: reason || 'Admin action' } })}
        loading={cancelling}
        title="Annuler le plan"
        description="Cette action annule l'abonnement catalogue du marchand. Cette opération est irréversible."
        confirmLabel="Annuler le plan"
        confirmColor="error"
      />
    </Box>
  );
}
